<template>
  <div>
    <v-teleport to="header">
      <el-button
        icon="el-icon-printer"
        size="medium"
        round
        @click="printSelection = null; printModalVisible = true">
        {{ $tc('general.print') }}
      </el-button>
    </v-teleport>

    <panel :title="$tc('menu.ranking_list')">
      <template #title-inline>
        <el-select
          v-model="selectedTournamentId"
          class="ranking-scope-select"
          @visible-change="onRankingScopeDropdownVisible"
          @change="onRankingScopeChange">
          <el-option
            :label="generalRankingLabel"
            :value="null" />
          <el-option
            v-for="t in tournaments"
            :key="t.id"
            :label="t.name"
            :value="t.id" />
        </el-select>
      </template>

      <template #actions>
        <div class="ranking-view-toggle">
          <button
            type="button"
            class="toggle-btn"
            :class="{ active: rankingView === 'competitors' }"
            @click="rankingView = 'competitors'">
            {{ $tc('tournament.competitors', 2) }}
          </button>
          <button
            type="button"
            class="toggle-btn"
            :class="{ active: rankingView === 'sports-associations' }"
            @click="rankingView = 'sports-associations'">
            {{ $tc('menu.ranking_sports_associations') }}
          </button>
        </div>
      </template>

      <error-box v-if="error" :loading="loading" @reload="load()" />

      <template v-if="!error">
        <div v-show="rankingView === 'competitors'" class="ranking-filters">
          <span class="filter-label">{{ $tc('general.club', 1) }}:</span>
          <el-select
            v-model="filterClubId"
            :placeholder="$tc('general.club', 1)"
            clearable
            filterable
            class="filter-select"
            @change="onFiltersChange">
            <el-option
              v-for="c in clubs"
              :key="c.id"
              :label="c.shortName || c.name"
              :value="c.id" />
          </el-select>
        </div>

        <div v-show="rankingView === 'competitors'" class="ranking-section" v-loading="loadingMembers">
          <members-table
            :data="rankingMembers"
            :show-is-active="false"
            :show-points="true"
            :show-medal-stats="true"
            :show-club="true"
            :can-edit="false"
            index-column-label="Redni broj"
            :show-rank-column="true"
            :rank-column-after-points="true"
            :start-index="membersRankStartIndex" />
          <el-pagination
            v-if="rankingMembersTotal > membersPerPage"
            class="ranking-pagination"
            :current-page="membersPage"
            :page-sizes="[20, 50, 100]"
            :page-size="membersPerPage"
            :total="rankingMembersTotal"
            layout="total, sizes, prev, pager, next"
            @size-change="onMembersPageSizeChange"
            @current-change="onMembersPageChange" />
        </div>

        <div v-show="rankingView === 'sports-associations'" class="ranking-section">
          <clubs-table
            :data="rankingClubs"
            :loading="loading"
            :hidden-fields="rankingClubsHiddenFields"
            :show-points="true"
            :show-medal-stats="true"
            :show-actions="false"
            :show-index-column="true"
            index-column-label="Redni broj"
            :show-rank-column="true"
            :rank-column-after-points="true" />
        </div>
      </template>
    </panel>

    <el-dialog
      title="Štampanje rang liste"
      :visible.sync="printModalVisible"
      width="600px"
      :close-on-click-modal="false"
      :close-on-press-escape="true"
      @close="onPrintModalClose">
      <div class="print-modal-content">
        <div class="step-content">
          <h3 class="step-title">Izaberite rang listu:</h3>
          <div class="selection-options">
            <div class="option-list">
              <div
                class="option-row"
                :class="{ 'is-selected': printSelection === 'competitors' }"
                @click="printSelection = 'competitors'"
              >
                <el-radio
                  v-model="printSelection"
                  label="competitors"
                  class="option-radio"
                  @click.native.stop
                >
                  {{ $tc('tournament.competitors', 2) }}
                </el-radio>
              </div>

              <div
                class="option-row"
                :class="{ 'is-selected': printSelection === 'sports-associations' }"
                @click="printSelection = 'sports-associations'"
              >
                <el-radio
                  v-model="printSelection"
                  label="sports-associations"
                  class="option-radio"
                  @click.native.stop
                >
                  {{ $tc('menu.ranking_sports_associations') }}
                </el-radio>
              </div>
            </div>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button :disabled="printing" @click="printModalVisible = false">Otkaži</el-button>
        <el-button
          type="primary"
          :disabled="printing || !printSelection"
          @click="onPrintChoice(printSelection)"
        >
          Štampaj
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import errorHandler from '@/common/errorHandler'
import { belts } from '@/common/definitions'
import { tableHtml } from '@/common/print'
import MembersTable from '@/components/members/MembersTable'
import ClubsTable from '@/components/clubs/ClubsTable'

const DEFAULT_PER_PAGE = 20

export default {
  components: {
    MembersTable,
    ClubsTable
  },

  data () {
    return {
      error: false,
      loading: false,
      loadingMembers: false,
      rankingView: 'competitors',
      selectedTournamentId: null,
      hasAppliedInitialRankingScope: false,
      filterClubId: null,
      membersPage: 1,
      membersPerPage: DEFAULT_PER_PAGE,
      rankingClubsHiddenFields: ['email', 'website', 'contact', 'mobile'],
      printing: false,
      printModalVisible: false,
      printSelection: null
    }
  },

  computed: {
    clubs () {
      return this.$store.getters['ranking/clubs'] || []
    },
    tournaments () {
      return this.$store.getters['ranking/tournaments'] || []
    },
    generalRankingLabel () {
      return 'Generalni rang'
    },
    selectedRankingScopeLabel () {
      if (!this.selectedTournamentId) return this.generalRankingLabel
      const selectedTournament = (this.tournaments || []).find(t => String(t.id) === String(this.selectedTournamentId))
      return selectedTournament?.name || this.generalRankingLabel
    },

    rankingClubs () {
      return this.$store.getters['ranking/clubs'] || []
    },

    rankingMembers () {
      const page = this.$store.getters['ranking/membersPage']
      return (page && page.data) ? page.data : []
    },

    rankingMembersTotal () {
      const page = this.$store.getters['ranking/membersPage']
      return (page && page.total) ? page.total : 0
    },

    membersRankStartIndex () {
      return (this.membersPage - 1) * this.membersPerPage
    }
  },

  watch: {
    rankingView (view) {
      if (view === 'competitors') {
        this.loadMembers()
      }
    },
    printModalVisible (visible) {
      if (visible) {
        this.printSelection = null
      }
    }
  },

  created () {
    this.load()
  },

  mounted () {
    this.$store.commit('app/set', { title: this.$tc('menu.ranking_list') })
  },

  methods: {
    async load () {
      this.loading = true
      this.error = false
      try {
        await this.$store.dispatch('ranking/loadTournaments')
        this.applyDefaultRankingScope()
        await this.$store.dispatch('ranking/loadClubs', this.getRankingScopeParams())
        if (this.rankingView === 'competitors') {
          await this.loadMembers()
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async loadMembers () {
      this.loadingMembers = true
      try {
        const params = {
          page: this.membersPage,
          per_page: this.membersPerPage
        }
        Object.assign(params, this.getRankingScopeParams())
        if (this.filterClubId) {
          params.clubId = this.filterClubId
        }
        await this.$store.dispatch('ranking/loadMembers', params)
      } finally {
        this.loadingMembers = false
      }
    },

    async onRankingScopeChange () {
      this.membersPage = 1
      this.filterClubId = null
      await this.load()
    },
    async onRankingScopeDropdownVisible (visible) {
      if (!visible) return
      await this.$store.dispatch('ranking/loadTournaments')
      this.applyDefaultRankingScope()
    },
    applyDefaultRankingScope () {
      if (this.hasAppliedInitialRankingScope) return
      const activeTournamentId = this.$store.state.app.activeTournament?.id
      if (activeTournamentId) {
        const activeExists = (this.tournaments || []).some(t => String(t.id) === String(activeTournamentId))
        if (activeExists) {
          this.selectedTournamentId = activeTournamentId
          this.hasAppliedInitialRankingScope = true
          return
        }
      }
      if (this.tournaments && this.tournaments.length > 0) {
        this.selectedTournamentId = this.tournaments[0].id
      }
      this.hasAppliedInitialRankingScope = true
    },
    getRankingScopeParams () {
      if (!this.selectedTournamentId) return {}
      return { tournamentId: this.selectedTournamentId }
    },
    onFiltersChange () {
      this.membersPage = 1
      this.loadMembers()
    },

    onMembersPageChange (page) {
      this.membersPage = page
      this.loadMembers()
    },

    onMembersPageSizeChange (size) {
      this.membersPerPage = size
      this.membersPage = 1
      this.loadMembers()
    },

    async onPrintChoice (command) {
      if (this.printing) return

      this.printModalVisible = false
      this.printing = true
      const loading = this.$loading()
      try {
        if (command === 'competitors') {
          await this.printCompetitorsRanking()
          return
        }
        if (command === 'sports-associations') {
          await this.printSportsAssociationsRanking()
          return
        }
      } catch (e) {
        errorHandler(e)
      } finally {
        loading.close()
        this.printing = false
      }
    },

    onPrintModalClose () {
      // Keep selection reset consistent with other print modals
      this.printSelection = null
    },

    escapeHtml (value) {
      if (value === null || value === undefined) return ''
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },

    formatDate (date) {
      if (!date) return '—'
      const m = moment(date)
      return m.isValid() ? m.format('LL') : '—'
    },

    sexLabel (isMale) {
      if (isMale === null || isMale === undefined) return '—'
      return isMale ? this.$tc('general.male') : this.$tc('general.female')
    },

    clubFilterLabel () {
      if (!this.filterClubId) return null
      const club = (this.clubs || []).find(c => c.id === this.filterClubId)
      return club ? (club.shortName || club.name) : null
    },

    async fetchAllMembersForPrint () {
      const perPage = 200
      const paramsBase = {
        page: 1,
        per_page: perPage
      }
      Object.assign(paramsBase, this.getRankingScopeParams())
      if (this.filterClubId) {
        paramsBase.clubId = this.filterClubId
      }

      const first = await axios.get('ranking/members', { params: paramsBase })
      const payload = first?.data || {}
      const total = Number(payload.total || 0)
      const firstPageData = Array.isArray(payload.data) ? payload.data : []

      const pages = Math.max(1, Math.ceil(total / perPage))
      if (pages === 1) return firstPageData

      const all = [...firstPageData]
      for (let page = 2; page <= pages; page++) {
        const res = await axios.get('ranking/members', {
          params: { ...paramsBase, page }
        })
        const pageData = Array.isArray(res?.data?.data) ? res.data.data : []
        all.push(...pageData)
      }
      return all
    },

    openPrintWindow (title, html) {
      const newWin = window.open('')
      if (!newWin) {
        this.$message.error('Popup blokiran. Dozvolite pop-up prozore da biste štampali.')
        return
      }
      newWin.document.write(tableHtml(title, html))
      newWin.document.close()
      newWin.focus()

      const waitForImages = () => {
        const imgs = Array.from(newWin.document.images || [])
        if (imgs.length === 0) return Promise.resolve()

        return Promise.all(imgs.map(img => new Promise(resolve => {
          if (img.complete) return resolve()
          img.onload = () => resolve()
          img.onerror = () => resolve()
        })))
      }

      const doPrint = async () => {
        // Ensure styles + images are applied before print dialog
        await waitForImages()
        // A small delay helps Safari/Chrome reliably paint images before printing
        setTimeout(() => {
          try {
            newWin.focus()
            newWin.print()
          } finally {
            setTimeout(() => {
              try { newWin.close() } catch {}
            }, 500)
          }
        }, 150)
      }

      if (newWin.document.readyState === 'complete') {
        doPrint()
      } else {
        newWin.onload = () => doPrint()
      }
    },

    async printCompetitorsRanking () {
      const members = await this.fetchAllMembersForPrint()
      const clubLabel = this.clubFilterLabel()
      const title = `${this.$tc('menu.ranking_list')} - ${this.$tc('tournament.competitors', 2)}`
      const scopeLabel = this.selectedRankingScopeLabel

      const headerParts = [
        `<div class="ranking-print-scope">${this.escapeHtml(scopeLabel)}</div>`
      ]
      if (clubLabel) {
        headerParts.push(`<div class="ranking-print-filter">${this.escapeHtml(this.$tc('general.club', 1))}: ${this.escapeHtml(clubLabel)}</div>`)
      }
      const headerHtml = headerParts.join('')

      const getNumericValue = (row, keys) => {
        for (const key of keys) {
          if (row && row[key] != null && row[key] !== '') {
            const value = Number(row[key])
            if (Number.isFinite(value)) return value
          }
        }
        return 0
      }
      const getTotalMedals = (row) => {
        if (row && row.totalMedals != null && row.totalMedals !== '') {
          const total = Number(row.totalMedals)
          if (Number.isFinite(total)) return total
        }
        return getNumericValue(row, ['goldMedals', 'medalsGold', 'gold_medals']) +
          getNumericValue(row, ['silverMedals', 'medalsSilver', 'silver_medals']) +
          getNumericValue(row, ['bronzeMedals', 'medalsBronze', 'bronze_medals'])
      }

      const rows = (members || []).map((m, idx) => {
        const avatarSrc = m?.avatar ? this.$file('avatars', m.avatar) : null
        const avatarHtml = `
          <div style="width:32px;height:32px;border-radius:4px;background:#f0f0f0;border:1px solid #dcdfe6;overflow:hidden;display:inline-flex;align-items:center;justify-content:center;">
            ${avatarSrc ? `<img alt="" src="${this.escapeHtml(avatarSrc)}" style="width:32px;height:32px;object-fit:cover;display:block;" />` : ''}
          </div>
        `

        const fullName = `${m?.firstName || ''} ${m?.lastName || ''}`.trim() || '—'
        const points = (m?.earnedPoints != null) ? m.earnedPoints : 0
        const totalMedals = getTotalMedals(m)
        const goldMedals = getNumericValue(m, ['goldMedals', 'medalsGold', 'gold_medals'])
        const goldPoints = getNumericValue(m, ['goldPoints', 'goldMedalPoints', 'pointsGold', 'gold_points'])
        const silverMedals = getNumericValue(m, ['silverMedals', 'medalsSilver', 'silver_medals'])
        const silverPoints = getNumericValue(m, ['silverPoints', 'silverMedalPoints', 'pointsSilver', 'silver_points'])
        const bronzeMedals = getNumericValue(m, ['bronzeMedals', 'medalsBronze', 'bronze_medals'])
        const bronzePoints = getNumericValue(m, ['bronzePoints', 'bronzeMedalPoints', 'pointsBronze', 'bronze_points'])

        return `
          <tr>
            <td>${idx + 1}.</td>
            <td>${avatarHtml}</td>
            <td>${this.escapeHtml(fullName)}</td>
            <td>${this.escapeHtml(m?.clubShortName || '—')}</td>
            <td>${this.escapeHtml(this.formatDate(m?.born))}</td>
            <td>${this.escapeHtml(this.sexLabel(m?.isMale))}</td>
            <td>${this.escapeHtml(belts[m?.belt] || '—')}</td>
            <td style="text-align:center;">${this.escapeHtml(totalMedals)}</td>
            <td style="text-align:center; width:58px;">${this.escapeHtml(goldMedals)}</td>
            <td style="text-align:center; white-space: nowrap; width:72px;">${this.escapeHtml(goldPoints)}</td>
            <td style="text-align:center; width:58px;">${this.escapeHtml(silverMedals)}</td>
            <td style="text-align:center; white-space: nowrap; width:72px;">${this.escapeHtml(silverPoints)}</td>
            <td style="text-align:center; width:58px;">${this.escapeHtml(bronzeMedals)}</td>
            <td style="text-align:center; white-space: nowrap; width:72px;">${this.escapeHtml(bronzePoints)}</td>
            <td style="text-align:right; white-space: nowrap;">${this.escapeHtml(points)}</td>
            <td>${idx + 1}.</td>
          </tr>
        `
      }).join('')

      const html = `
        <style>
          @media print {
            @page { size: A4 landscape; }
          }
          .ranking-print-table {
            width: 100%;
            table-layout: fixed;
            font-size: 10px;
          }
          .ranking-print-scope {
            text-align: center;
            font-size: 18px;
            font-weight: 400;
            margin: 6px 0 12px;
          }
          .ranking-print-filter {
            font-size: 14px;
            font-weight: 500;
            margin: 0 0 10px;
          }
          .ranking-print-table th,
          .ranking-print-table td {
            padding: 5px 6px;
            white-space: normal !important;
            word-break: break-word;
          }
        </style>
        ${headerHtml}
        <table class="ranking-print-table">
          <thead>
            <tr>
              <th style="white-space: nowrap;">Redni broj</th>
              <th style="white-space: nowrap;">${this.escapeHtml(this.$tc('form.image'))}</th>
              <th style="white-space: nowrap;">${this.escapeHtml(this.$tc('form.name'))}</th>
              <th style="white-space: nowrap;">${this.escapeHtml(this.$tc('general.club', 1))}</th>
              <th style="white-space: nowrap;">${this.escapeHtml(this.$tc('form.date_of_birth'))}</th>
              <th style="white-space: nowrap;">${this.escapeHtml(this.$tc('form.gender'))}</th>
              <th style="white-space: nowrap;">${this.escapeHtml(this.$tc('form.belt'))}</th>
              <th style="white-space: nowrap; text-align:center;">Ukupno medalja</th>
              <th style="white-space: nowrap; text-align:center; width:58px;" title="Zlatne medalje">
                <span style="display:inline-flex;flex-direction:column;align-items:center;line-height:1.1;">
                  <span>Broj</span>
                  <span>🥇</span>
                </span>
              </th>
              <th style="white-space: nowrap; text-align:center; width:72px;">Poeni 🥇</th>
              <th style="white-space: nowrap; text-align:center; width:58px;" title="Srebrne medalje">
                <span style="display:inline-flex;flex-direction:column;align-items:center;line-height:1.1;">
                  <span>Broj</span>
                  <span>🥈</span>
                </span>
              </th>
              <th style="white-space: nowrap; text-align:center; width:72px;">Poeni 🥈</th>
              <th style="white-space: nowrap; text-align:center; width:58px;" title="Bronzane medalje">
                <span style="display:inline-flex;flex-direction:column;align-items:center;line-height:1.1;">
                  <span>Broj</span>
                  <span>🥉</span>
                </span>
              </th>
              <th style="white-space: nowrap; text-align:center; width:72px;">Poeni 🥉</th>
              <th style="white-space: nowrap; text-align:right;">Ukupno poena</th>
              <th style="white-space: nowrap;">${this.escapeHtml(this.$tc('general.rank'))}</th>
            </tr>
          </thead>
          <tbody>
            ${rows || ''}
          </tbody>
        </table>
      `

      this.openPrintWindow(title, html)
    },

    async printSportsAssociationsRanking () {
      if (!this.rankingClubs || this.rankingClubs.length === 0) {
        await this.$store.dispatch('ranking/loadClubs', this.getRankingScopeParams())
      }

      const title = `${this.$tc('menu.ranking_list')} - ${this.$tc('menu.ranking_sports_associations')}`
      const scopeLabel = this.selectedRankingScopeLabel
      const rows = (this.rankingClubs || []).map((c, idx) => {
        const logoSrc = c?.logo ? this.$file('avatars', c.logo) : null
        const logoHtml = logoSrc
          ? `<img alt="" src="${this.escapeHtml(logoSrc)}" style="width:32px;height:32px;object-fit:contain;" />`
          : '—'

        const points = (c?.points != null) ? c.points : 0
        const getNumericValue = (row, keys) => {
          for (const key of keys) {
            if (row && row[key] != null && row[key] !== '') {
              const value = Number(row[key])
              if (Number.isFinite(value)) return value
            }
          }
          return 0
        }
        const totalMedals = (c?.totalMedals != null && c?.totalMedals !== '')
          ? Number(c.totalMedals)
          : getNumericValue(c, ['goldMedals', 'medalsGold', 'gold_medals']) +
            getNumericValue(c, ['silverMedals', 'medalsSilver', 'silver_medals']) +
            getNumericValue(c, ['bronzeMedals', 'medalsBronze', 'bronze_medals'])
        const goldMedals = getNumericValue(c, ['goldMedals', 'medalsGold', 'gold_medals'])
        const goldPoints = getNumericValue(c, ['goldPoints', 'goldMedalPoints', 'pointsGold', 'gold_points'])
        const silverMedals = getNumericValue(c, ['silverMedals', 'medalsSilver', 'silver_medals'])
        const silverPoints = getNumericValue(c, ['silverPoints', 'silverMedalPoints', 'pointsSilver', 'silver_points'])
        const bronzeMedals = getNumericValue(c, ['bronzeMedals', 'medalsBronze', 'bronze_medals'])
        const bronzePoints = getNumericValue(c, ['bronzePoints', 'bronzeMedalPoints', 'pointsBronze', 'bronze_points'])
        return `
          <tr>
            <td>${idx + 1}.</td>
            <td>${logoHtml}</td>
            <td>${this.escapeHtml(c?.name || '—')}</td>
            <td>${this.escapeHtml(c?.shortName || '—')}</td>
            <td>${this.escapeHtml(c?.city || '—')}</td>
            <td style="text-align:center;">${this.escapeHtml(totalMedals)}</td>
            <td style="text-align:center; width:58px;">${this.escapeHtml(goldMedals)}</td>
            <td style="text-align:center; white-space: nowrap; width:72px;">${this.escapeHtml(goldPoints)}</td>
            <td style="text-align:center; width:58px;">${this.escapeHtml(silverMedals)}</td>
            <td style="text-align:center; white-space: nowrap; width:72px;">${this.escapeHtml(silverPoints)}</td>
            <td style="text-align:center; width:58px;">${this.escapeHtml(bronzeMedals)}</td>
            <td style="text-align:center; white-space: nowrap; width:72px;">${this.escapeHtml(bronzePoints)}</td>
            <td style="text-align:right; white-space: nowrap;">${this.escapeHtml(points)}</td>
            <td>${idx + 1}.</td>
          </tr>
        `
      }).join('')

      const html = `
        <style>
          @media print {
            @page { size: A4 landscape; }
          }
          .ranking-print-table {
            width: 100%;
            table-layout: fixed;
            font-size: 10px;
          }
          .ranking-print-scope {
            text-align: center;
            font-size: 18px;
            font-weight: 400;
            margin: 6px 0 12px;
          }
          .ranking-print-table th,
          .ranking-print-table td {
            padding: 5px 6px;
            white-space: normal !important;
            word-break: break-word;
          }
        </style>
        <div class="ranking-print-scope">${this.escapeHtml(scopeLabel)}</div>
        <table class="ranking-print-table">
          <thead>
            <tr>
              <th style="white-space: nowrap;">Redni broj</th>
              <th style="white-space: nowrap;">${this.escapeHtml(this.$tc('settings.logo'))}</th>
              <th style="white-space: nowrap;">${this.escapeHtml(this.$tc('club.club_name'))}</th>
              <th style="white-space: nowrap;">${this.escapeHtml(this.$tc('club.short_name'))}</th>
              <th style="white-space: nowrap;">${this.escapeHtml(this.$tc('form.city'))}</th>
              <th style="white-space: nowrap; text-align:center;">Ukupno medalja</th>
              <th style="white-space: nowrap; text-align:center; width:58px;" title="Zlatne medalje">
                <span style="display:inline-flex;flex-direction:column;align-items:center;line-height:1.1;">
                  <span>Broj</span>
                  <span>🥇</span>
                </span>
              </th>
              <th style="white-space: nowrap; text-align:center; width:72px;">Poeni 🥇</th>
              <th style="white-space: nowrap; text-align:center; width:58px;" title="Srebrne medalje">
                <span style="display:inline-flex;flex-direction:column;align-items:center;line-height:1.1;">
                  <span>Broj</span>
                  <span>🥈</span>
                </span>
              </th>
              <th style="white-space: nowrap; text-align:center; width:72px;">Poeni 🥈</th>
              <th style="white-space: nowrap; text-align:center; width:58px;" title="Bronzane medalje">
                <span style="display:inline-flex;flex-direction:column;align-items:center;line-height:1.1;">
                  <span>Broj</span>
                  <span>🥉</span>
                </span>
              </th>
              <th style="white-space: nowrap; text-align:center; width:72px;">Poeni 🥉</th>
              <th style="white-space: nowrap; text-align:right;">Ukupno poena</th>
              <th style="white-space: nowrap;">${this.escapeHtml(this.$tc('general.rank'))}</th>
            </tr>
          </thead>
          <tbody>
            ${rows || ''}
          </tbody>
        </table>
      `

      this.openPrintWindow(title, html)
    }
  }
}
</script>

<style lang="scss" scoped>
.ranking-scope-select {
  width: 280px;
}

::v-deep .ranking-scope-select .el-input__inner {
  height: 44px;
  line-height: 44px;
  border-radius: 22px;
  border: 1px solid #dcdfe6;
  color: #606266;
  background: #fff;
  padding-right: 34px;
}

::v-deep .ranking-scope-select .el-input__icon {
  line-height: 44px;
}

.print-modal-content {
  min-height: 200px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.step-content {
  padding: 10px 0;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.step-title {
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.selection-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.option-list {
  width: 100%;
  box-sizing: border-box;
}

.option-row {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  background-color: #fafafa;
  border-radius: 4px;
  margin: 4px 0;
  transition: background-color 0.2s;
  border: 1px solid transparent;

  &:hover {
    background-color: #f0f0f0;
  }

  &.is-selected {
    background-color: #fff;
    border-color: #dcdfe6;
  }
}

.option-radio {
  width: 100%;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.ranking-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  margin-bottom: 24px;

  .filter-label {
    font-size: 14px;
    color: var(--secondary2, #606266);
  }

  .filter-select {
    width: 200px;
  }
}

.ranking-pagination {
  margin-top: 16px;
  justify-content: flex-start;
}

.ranking-view-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0;
  border: 1px solid #e0e0e0;
  border-radius: 22px;
  overflow: hidden;
  background: #f8f8f8;
  min-width: 280px;

  .toggle-btn {
    flex: 1;
    min-width: 0;
    height: 44px;
    padding: 0 24px;
    font-size: 14px;
    font-weight: 700;
    border: none;
    border-right: 1px solid #e0e0e0;
    margin: 0;
    cursor: pointer;
    background: #f8f8f8;
    color: #4d4d4d;
    transition: background 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &::before {
      display: none;
    }

    &:first-child {
      border-top-left-radius: 22px;
      border-bottom-left-radius: 22px;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    &:last-child {
      border-top-right-radius: 22px;
      border-bottom-right-radius: 22px;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-right: none;
    }

    &:hover:not(.active) {
      background: #f0f0f0;
      color: #4d4d4d;
    }

    &.active,
    &.active:hover {
      background-color: #fff;
      color: #4d4d4d;
    }
  }
}

.ranking-section {
  margin-bottom: 32px;

  .ranking-section-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 12px 0;
  }
}
</style>
